import _ from 'lodash';
import React, { Component, useState } from 'react';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import Media from 'react-media';
import Accordion from '../LazyWidgets/Accordion';
import CircleText from '../LazyWidgets/CircleText';
import Modal from '../LazyWidgets/Modal';
import Pagination from '../LazyWidgets/Pagination';
import Table from '../LazyWidgets/Table';
import CollapsibleCard from '../CollapsibleCard';
import s from './LearningObjectives.module.css';
import common from '../../styles/common-styles.module.css';
import MLIcon from '../MLIcon';
import { LEGEND_COLORS, GREY_TEXT, AQUA_BASE } from '../../src/ReportsTabs/reports-constants';
import LevelDown from './level_down.svg';
import ResponsiveLearningObjective from './ResponsiveLearningObjective';
import ColorBlock from '../ColorBlock/ColorBlock';
import { clickLODetails } from '../../src/analyticsEvents';
import { sendAnalyticsEvent } from '../../src/actions/reports';

const CollapsibleRow = ({
  row,
  tableData,
  primaryAccessor,
  tableHeader,
  getCircleTextElement,
  openedAccordions,
  reportType,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);

  const dispatch = useDispatch();

  const handleOnChange = () => {
    setIsCollapsed(!isCollapsed);
    dispatch(sendAnalyticsEvent(clickLODetails(reportType)));
  };

  return (
    <Accordion
      selectedItemsIndex={openedAccordions}
      onChange={handleOnChange}
    >
      <Accordion.AccordionItem>
        <Accordion.AccordionTitle
          className={s.qqAccordionTitle}
          data-test-id={`${reportType}-performance-by-individual-lo`}
          iconClosed={{ type: 'chevron_right', fill: AQUA_BASE }}
          iconOpen={{ type: 'chevron_down', fill: AQUA_BASE }}
        >
          <MLIcon
            type="folder"
            role="presentation"
            width="18px"
            height="18px"
            color={GREY_TEXT}
            className={s.qqAccordionFolderIcon}
          />
          <div className={s.qqAccordionTitleName}>
            {row.name}
          </div>
          <div>
            {getCircleTextElement(row, tableData.data.length, null, primaryAccessor)}
          </div>
        </Accordion.AccordionTitle>
        <Accordion.AccordionBody active={!isCollapsed} className={s.qqAccordionBody}>
          {<Table
            className={s.qqAccordionTable}
            data={tableData.data}
            columns={tableHeader}
            styling={tableData.lOTableStyle}
            ruled
            autosort={false}
            minWidth={300}
          />}
        </Accordion.AccordionBody>
      </Accordion.AccordionItem>
    </Accordion>
  );
};

CollapsibleRow.propTypes = {
  row: PropTypes.object,
  tableData: PropTypes.object,
  primaryAccessor: PropTypes.object,
  tableHeader: PropTypes.array,
  getCircleTextElement: PropTypes.func,
  openedAccordions: PropTypes.array,
  reportType: PropTypes.string,
};

class LearningObjectives extends Component {

  static getDerivedStateFromProps(newProps, prevState) {
    if (!_.isEqual(newProps.data, prevState.rawData)) {
      const originalData = _.filter(newProps.data, (row) => {
        const condition = row.hasQuestions || !_.some(row.children, { denominator: 0 });
        return condition;
      }) || [];
      return ({
        rawData: newProps.data,
        learningObjectiveData: originalData,
        lOPaginatedData: originalData.slice(0, prevState.pageSize) || [],
        paginatedActivePage: 1,
        openedAccordions: [],
      });
    }
    return null;
  }

  constructor(props) {
    super(props);
    const originalData = _.filter(props.data, (row) => {
      const condition = row.hasQuestions || !_.some(row.children, { denominator: 0 });
      return condition;
    }) || [];
    const pageSize = 10;
    this.state = {
      pageSize,
      rawData: props.data,
      learningObjectiveData: originalData,
      paginatedActivePage: 1,
      lOPaginatedData: originalData.slice(0, pageSize) || [],
      openedAccordions: [],
    };
    this.getLOCard = this.getLOCard.bind(this);
    this.getLOTableData = this.getLOTableData.bind(this);
    this.getCircleTextElement = this.getCircleTextElement.bind(this);
    this.paginate = this.paginate.bind(this);
    this.getLOPercentageColor = this.getLOPercentageColor.bind(this);
    this.getRange = this.getRange.bind(this);
  }

  getRange(value) {
    if (value >= 0.80) {
      return 'high';
    }

    if (value >= 0.60) {
      return 'intermediate';
    }

    return 'low';
  }

  getLOPercentageColor = (value) => {
    if (value >= 0.80) {
      return undefined;
    }

    if (value >= 0.60) {
      return LEGEND_COLORS.RANGE_MEDIUM;
    }

    return LEGEND_COLORS.RANGE_LOW;
  };

  getLOTableData(row, primaryAccessor) {
    const { accessors, cellStyling } = this.props;
    const stylingObj = [];
    const dataToMap = row.children || [];
    const hasFirstRow = _.filter(dataToMap, item => item.isFirst);

    if (row.hasQuestions && _.isEmpty(hasFirstRow)) {
      const {
        children, // eslint-disable-line no-unused-vars
        ...rowToCopy
      } = row;
      const tableFirstRow = {
        ...rowToCopy,
        isFirst: true,
      };
      dataToMap.unshift(tableFirstRow);
    }

    const data = dataToMap.map((cell, index) => {
      const stylingRow = {
        row: `row-${index}`,
        backgroundColor: '#fff',
        fontSize: '15px',
        cells: cellStyling || [],
      };

      stylingObj.push(stylingRow);

      const finalObj = {
        className: `row-${index}`,
        isFirst: cell.isFirst || false,
        isNullState: cell.isNullState,
      };

      accessors.forEach((item) => {
        finalObj[item.accessor] = cell[item.accessor];
        finalObj[item.sortValue] = cell[item.sortValue];
      });

      return finalObj;
    });

    let sortableData = [];
    if (row.hasQuestions) {
      const firstRow = data[0];
      sortableData = _.sortBy(data.slice(1), option => (option[primaryAccessor.sortValue]));
      sortableData.unshift(firstRow);
    } else {
      sortableData = _.sortBy(data, option => (option[primaryAccessor.sortValue]));
    }

    return {
      lOTableStyle: stylingObj,
      data: sortableData || [],
    };
  }

  getCircleTextElement(row, amount, className, primaryAccessor) {
    const classes = clsx(
      s.qqAccordionSubText,
      className,
      { [s.noLowScore]: row.lowScoring === 0 },
    );

    if (row.lowScoring > 0) {
      const circleColor = this.getLOPercentageColor(row[primaryAccessor.sortValue]);
      return (
        <CircleText circleAriaHidden className={classes} circleSize={circleColor ? 15 : 0} circleColor={circleColor}>
          {this.props.getSubtitleLOText(row, amount)}
        </CircleText>
      );
    }
    return (
      <div className={classes}>
        {`${amount} Learning Objective${amount > 1 ? 's' : ''}`}
      </div>
    );
  }

  getLOCard() {
    const { id, className, accessors, modal, nullStateMessage, cardTitle, primaryMeasureAccessor, reportType } = this.props;
    const {
      learningObjectiveData,
      paginatedActivePage,
      lOPaginatedData,
      openedAccordions,
    } = this.state;

    const primaryAccessor = _.find(accessors, { accessor: primaryMeasureAccessor }) || accessors[0];

    const classes = clsx(
      s.qqLOCardContainer,
      className,
    );
    const tableHeader = [];
    accessors.forEach((col, i) => {
      const column = {
        accessor: col.accessor,
        Header: col.Header,
      };
      if (i === 0) {
        column.cell = (item) => {
          const icon = (
            <div className={s.qqdiv}>
              {(!item.isFirst) &&
                <img
                  className={s.qqLevelDownIcon}
                  src={LevelDown}
                  alt="level down"
                />
              }
              <div className={s.qqSubmoduleName}>
                {item[col.accessor]}
              </div>
            </div>);
          return icon;
        };
      }
      if (col.accessor === primaryAccessor.accessor) {
        column.cell = (item) => {
          if (item.isNullState) {
            return (
              <div>{item[col.accessor]}
                <span className={common.screenReaderText}>no data</span>
              </div>
            );
          }
          return (
            <div>
              <ColorBlock
                value={item[col.sortValue]}
                mediumRangeMinValue={0.6}
                highRangeMinValue={0.8}
                className={s.colorBlock}
                aria-label={`${this.getRange(item[col.sortValue])} performance`}
              >
                {item[col.accessor]}
              </ColorBlock>
            </div>
          );
        };
      }
      tableHeader.push(column);
    });

    return (
      <CollapsibleCard
        id={id}
        title={cardTitle || 'Performance by Learning Objective'}
        className={classes}
        data-test-id={`${reportType}-performance-by-lo`}
        reportType={reportType}
      >
        {(nullStateMessage) ||
          (<div>
            <div className={s.qqLOTitleContainer}>
              <Modal className={s.qqLOModalContainer} trigger={<button role="link" className={s.qqLOLink}>How is this calculated?</button>}>
                <Modal.Header className={s.qqLOModalHeader}>
                  {modal.title}
                </Modal.Header>
                <Modal.Content className={s.qqModalContent}>
                  {modal.content}
                </Modal.Content>
              </Modal>
            </div>
            <div className={s.qqLOBodyContainer}>
              <Media query={{ minWidth: 710 }}>
                {matches =>
                  (matches ? (
                    <div className={s.qqLOBodyContainer}>
                      <Table className={s.qqTableHeader} columns={tableHeader} minWidth={300} aria-hidden />
                      <div className={s.qqAccordionContainer}>
                        {lOPaginatedData.map((row) => {
                          const tableData = this.getLOTableData(row, primaryAccessor);
                          return (
                            <CollapsibleRow
                              key={`asignment-${row.id}`}
                              row={row}
                              tableData={tableData}
                              primaryAccessor={primaryAccessor}
                              tableHeader={tableHeader}
                              openedAccordions={openedAccordions}
                              getCircleTextElement={this.getCircleTextElement}
                              reportType={reportType}
                            />
                          );
                        })}
                        {(learningObjectiveData.length > this.state.pageSize) &&
                          <Pagination
                            className={s.qqLOPagination}
                            activePage={paginatedActivePage}
                            boundaryRange={10}
                            onPageChange={this.paginate}
                            totalPages={Math.ceil(learningObjectiveData.length / this.state.pageSize)}
                            siblingRange={0}
                            firstItem={null}
                            lastItem={null}
                          />
                        }
                      </div>
                    </div>
                  ) : (
                    <ResponsiveLearningObjective
                      learningObjectiveData={learningObjectiveData}
                      lOPaginatedData={lOPaginatedData}
                      pageSize={this.state.pageSize}
                      paginatedActivePage={paginatedActivePage}
                      getTableData={this.getLOTableData}
                      getCircleTextElement={this.getCircleTextElement}
                      tableColumns={tableHeader}
                      primaryAccessor={primaryAccessor}
                    />
                    ))}
              </Media>
            </div>
          </div>)}
      </CollapsibleCard>
    );
  }

  paginate(e, obj) {
    const start = (obj.activePage - 1) * this.state.pageSize;
    const end = start + this.state.pageSize;
    this.setState({
      lOPaginatedData: this.state.learningObjectiveData.slice(start, end),
      paginatedActivePage: obj.activePage,
      openedAccordions: [],
    });
  }

  render() {
    return (
      <div className={s.qqLOCardContainer}>
        {this.getLOCard()}
      </div>
    );
  }
}

LearningObjectives.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  loading: PropTypes.bool,
  data: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
  ]).isRequired,
  onTabChange: PropTypes.func,
  reportType: PropTypes.string,
  accessors: PropTypes.array,
  modal: PropTypes.object,
  nullStateMessage: PropTypes.object,
  cardTitle: PropTypes.string,
  getSubtitleLOText: PropTypes.func,
  primaryMeasureAccessor: PropTypes.string,
  cellStyling: PropTypes.array,
};

export default LearningObjectives;
